<script setup lang="ts">
const { t } = useI18n()
const returnWindowDays = computed(() => +useRuntimeConfig().public.returnWindowDays)
</script>

<template>
  <div class="relative overflow-hidden py-16 sm:py-24">
    <!-- Background decoration -->
    <div class="absolute inset-0 -z-10">
      <div class="absolute inset-0 from-slate-50 via-white to-slate-50/50 bg-gradient-to-br" />
      <div class="absolute left-1/2 top-0 opacity-30 blur-3xl -translate-x-1/2">
        <div class="aspect-[1155/678] w-[72.1875rem] from-error to-[#ff80b5] bg-gradient-to-tr" style="clip-path: polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)" />
      </div>
    </div>

    <div class="relative mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8 sm:px-6">
      <!-- Header Section -->
      <section aria-labelledby="details-heading" class="text-center">
        <div class="relative inline-flex">
          <div class="absolute inset-0 animate-pulse from-error/20 via-rose-500/20 to-error/20 bg-gradient-to-r blur-xl" />
          <img
            src="~/assets/icons/icon-warranty-light.svg"
            alt="Warranty"
            class="relative mx-auto h-28 w-auto transform drop-shadow-2xl transition-transform duration-300 sm:h-32 hover:scale-105"
            loading="lazy"
          >
        </div>

        <h2 id="details-heading" class="mt-8 text-4xl text-gray-900 font-bold tracking-tight sm:text-5xl">
          {{ t('policies.satisfaction_guarantee.title') }}
        </h2>
        <p class="mx-auto mt-4 max-w-3xl text-lg text-gray-600 leading-relaxed sm:text-xl">
          {{ t('policies.satisfaction_guarantee.subtitle') }}
        </p>
      </section>

      <!-- Features Grid -->
      <section aria-labelledby="policy-heading" class="mt-16 lg:mt-20">
        <h2 id="policy-heading" class="sr-only">
          {{ t('policies.satisfaction_guarantee.title') }}
        </h2>

        <div class="grid grid-cols-1 gap-8 lg:grid-cols-3 sm:grid-cols-2 lg:gap-12">
          <!-- Courier Delivery Card -->
          <div class="group relative animate-delay-0 sm:animate-fade-in-scale-up">
            <div class="relative h-full overflow-hidden border border-gray-100/50 rd-md bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <!-- Gradient Background -->
              <div class="absolute inset-0 from-blue-500/10 via-indigo-500/5 to-purple-500/10 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

              <!-- Content -->
              <div class="relative z-10">
                <!-- Icon Container -->
                <div class="relative inline-flex">
                  <div class="absolute inset-0 rounded-2xl from-blue-500/20 to-indigo-500/20 bg-gradient-to-br blur-xl transition-all duration-300 group-hover:blur-2xl" />
                  <div class="relative rounded-2xl bg-white/80 p-4 shadow-lg backdrop-blur-sm">
                    <img
                      src="~/assets/icons/icon-planet-light.svg"
                      alt=""
                      class="h-16 w-auto transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    >
                  </div>
                </div>

                <!-- Text Content -->
                <h3 class="mt-6 text-xl text-gray-900 font-semibold transition-colors duration-300 group-hover:text-error">
                  {{ t('policies.satisfaction_guarantee.courier_delivery.title') }}
                </h3>
                <p class="mt-3 text-base text-gray-600 leading-relaxed">
                  {{ t('policies.satisfaction_guarantee.courier_delivery.description') }}
                </p>

                <!-- Decorative Element -->
                <div class="absolute bottom-4 right-4 h-20 w-20 rounded-full from-blue-500/10 via-indigo-500/5 to-purple-500/10 bg-gradient-to-br opacity-5 transition-all duration-300 group-hover:scale-150 group-hover:opacity-10" />
              </div>
            </div>
          </div>

          <!-- Customer Support Card -->
          <div class="group relative animate-delay-100 sm:animate-fade-in-scale-up">
            <div class="relative h-full overflow-hidden border border-gray-100/50 rd-md bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <!-- Gradient Background -->
              <div class="absolute inset-0 from-error/10 via-rose-500/5 to-pink-500/10 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

              <!-- Content -->
              <div class="relative z-10">
                <!-- Icon Container -->
                <div class="relative inline-flex">
                  <div class="absolute inset-0 rounded-2xl from-error/20 to-rose-500/20 bg-gradient-to-br blur-xl transition-all duration-300 group-hover:blur-2xl" />
                  <div class="relative rounded-2xl bg-white/80 p-4 shadow-lg backdrop-blur-sm">
                    <img
                      src="~/assets/icons/icon-chat-light.svg"
                      alt=""
                      class="h-16 w-auto transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    >
                  </div>
                </div>

                <!-- Text Content -->
                <h3 class="mt-6 text-xl text-gray-900 font-semibold transition-colors duration-300 group-hover:text-error">
                  {{ t('policies.satisfaction_guarantee.customer_support.title') }}
                </h3>
                <p class="mt-3 text-base text-gray-600 leading-relaxed">
                  {{ t('policies.satisfaction_guarantee.customer_support.description') }}
                </p>

                <!-- Decorative Element -->
                <div class="absolute bottom-4 right-4 h-20 w-20 rounded-full from-error/10 via-rose-500/5 to-pink-500/10 bg-gradient-to-br opacity-5 transition-all duration-300 group-hover:scale-150 group-hover:opacity-10" />
              </div>
            </div>
          </div>

          <!-- Returns Card -->
          <div class="group relative animate-delay-200 sm:animate-fade-in-scale-up">
            <div class="relative h-full overflow-hidden border border-gray-100/50 rd-md bg-white p-8 shadow-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
              <!-- Gradient Background -->
              <div class="absolute inset-0 from-emerald-500/10 via-teal-500/5 to-cyan-500/10 bg-gradient-to-br opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
              <!-- Content -->
              <div class="relative z-10">
                <!-- Icon Container -->
                <div class="relative inline-flex">
                  <div class="absolute inset-0 rounded-2xl from-emerald-500/20 to-teal-500/20 bg-gradient-to-br blur-xl transition-all duration-300 group-hover:blur-2xl" />
                  <div class="relative rounded-2xl bg-white/80 p-4 shadow-lg backdrop-blur-sm">
                    <img
                      src="~/assets/icons/icon-returns-light.svg"
                      alt=""
                      class="h-16 w-auto transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    >
                  </div>
                </div>

                <!-- Text Content -->
                <h3 class="mt-6 text-xl text-gray-900 font-semibold transition-colors duration-300 group-hover:text-error">
                  {{ t('policies.satisfaction_guarantee.returns.title') }}
                </h3>
                <p class="mt-3 text-base text-gray-600 leading-relaxed">
                  {{ t('policies.satisfaction_guarantee.returns.description', [returnWindowDays]) }}
                </p>

                <!-- Decorative Element -->
                <div class="absolute bottom-4 right-4 h-20 w-20 rounded-full from-emerald-500/10 via-teal-500/5 to-cyan-500/10 bg-gradient-to-br opacity-5 transition-all duration-300 group-hover:scale-150 group-hover:opacity-10" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
